import { AddTeamRequest } from 'api';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HttpError, MessageProps } from 'types';

export const useTeamMessageProps = (
  status: 'error' | 'success' | 'idle' | 'loading',
  error: unknown,
  payload?: AddTeamRequest | undefined
): MessageProps => {
  const { t } = useTranslation('manage_organisation');

  const messagePropsLookup = useMemo(() => {
    const memorizedMessageProps: Record<string, MessageProps> = {
      idle: {
        messageTitle: t('team_idle_message', ''),
        messageStatus: 'info',
      },
      error: {
        messageTitle: t(
          'team_error_message',
          'The team could not be created. Please try again.'
        ),
        messageStatus: 'error',
      },
      success: {
        messageTitle: t(
          'team_created_success_message',
          'The team has been successfully created.'
        ),
        messageStatus: 'success',
      },
    };

    return memorizedMessageProps;
  }, [t]);

  const [messageProps, setMessageProps] = useState<MessageProps>(messagePropsLookup.idle);

  useEffect(() => {
    const errorMessageMapping = (property: string, errMsg: string) => {
      switch (property) {
        case 'TeamName':
          return t('team_duplicate_teamname_message', errMsg);
        case 'BackOfficeTeamName':
          return t('team_duplicate_backofficecode_message', errMsg);
        default:
          return errMsg;
      }
    };

    if (status !== 'loading') {
      const responseError = error as HttpError;
      const message = messagePropsLookup[status];
      if (responseError?.errors && status === 'error') {
        const errors: string[] = [];
        for (const err in responseError.errors) {
          const errMsg = errorMessageMapping(err, responseError.errors[err][0]);
          errors.push(errMsg);
        }
        message.messageErrors = errors;
      }

      if (status === 'success' && payload?.teamName) {
        message.messageTitle = `${t('team', 'Team')} ${payload.teamName} ${t(
          'successfully_added',
          'successfully added'
        )}.`;
      }

      if (
        messageProps.messageStatus !== message.messageStatus &&
        messageProps.messageTitle !== message.messageTitle
      ) {
        setMessageProps(message);
      }
    }
  }, [
    status,
    error,
    messagePropsLookup,
    setMessageProps,
    t,
    payload,
    messageProps.messageStatus,
    messageProps.messageTitle,
  ]);

  return messageProps;
};
